import { FileText, CheckCircle, Bookmark, LayoutGrid, Shield, Flag, Upload, MessageSquare } from "lucide-react";
import { cn } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";

type FilterTab = "all" | "drafts" | "published" | "favorites" | "admin-review" | "admin-reports" | "admin-bulk-upload" | "admin-requests";

interface DashboardSidebarProps {
  activeTab: FilterTab;
  onTabChange: (tab: FilterTab) => void;
  draftsCount: number;
  publishedCount: number;
  savedCount: number;
  isAdmin?: boolean;
  pendingCount?: number;
}

const tabs: Array<{ id: FilterTab; label: string; icon: any }> = [
  { id: "all", label: "All Macros", icon: LayoutGrid },
  { id: "drafts", label: "Drafts", icon: FileText },
  { id: "published", label: "Published", icon: CheckCircle },
  { id: "favorites", label: "Saved", icon: Bookmark },
];

const adminTabs: Array<{ id: FilterTab; label: string; icon: any }> = [
  { id: "admin-review", label: "Review Queue", icon: Shield },
  { id: "admin-reports", label: "Reports", icon: Flag },
  { id: "admin-bulk-upload", label: "Bulk Upload", icon: Upload },
  { id: "admin-requests", label: "Macro Requests", icon: MessageSquare },
];

export const DashboardSidebar = ({
  activeTab,
  onTabChange,
  draftsCount,
  publishedCount,
  savedCount,
  isAdmin = false,
  pendingCount = 0,
}: DashboardSidebarProps) => {
  const getCount = (tabId: FilterTab) => {
    if (tabId === "drafts") return draftsCount;
    if (tabId === "published") return publishedCount;
    if (tabId === "favorites") return savedCount;
    if (tabId === "admin-review") return pendingCount;
    return null;
  };

  return (
    <aside className="hidden md:block w-56 h-[calc(100vh-6rem)] fixed top-24 sm:top-28 lg:top-32 left-6 md:left-8 lg:left-12 overflow-y-auto">
      <div className="py-4 pr-4 pl-0">
        <h2 className="text-lg font-semibold mb-4 px-0">Dashboard</h2>
        <nav className="space-y-1">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const count = getCount(tab.id);
            const isActive = activeTab === tab.id;

            return (
              <button
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                className={cn(
                  "w-full flex items-center gap-3 pl-0 pr-3 py-2 rounded-lg text-sm font-medium transition-colors",
                  isActive
                    ? "text-black dark:text-white"
                    : "text-muted-foreground hover:text-black dark:hover:text-white"
                )}
              >
                <Icon className="h-4 w-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Admin Section */}
        {isAdmin && (
          <>
            <Separator className="my-4" />
            <h3 className="text-sm font-medium text-muted-foreground mb-2 px-0">ADMIN</h3>
            <nav className="space-y-1">
              {adminTabs.map((tab) => {
                const Icon = tab.icon;
                const count = getCount(tab.id);
                const isActive = activeTab === tab.id;

                return (
                  <button
                    key={tab.id}
                    onClick={() => onTabChange(tab.id)}
                    className={cn(
                      "w-full flex items-center gap-3 pl-0 pr-3 py-2 rounded-lg text-sm font-medium transition-colors",
                      isActive
                        ? "text-black dark:text-white"
                        : "text-muted-foreground hover:text-black dark:hover:text-white"
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </>
        )}
      </div>
    </aside>
  );
};
